import { AlertCircle, ArrowUpRight, CheckCircle2, Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  ExternalNoteActionSchema,
  ExternalNoteActionStatus,
} from '@/services/types/external-note-types';

import { CmsPushDetailModal } from './CmsPushDetailModal';

const STATUS_CONFIG: Record<
  ExternalNoteActionStatus,
  {
    icon: React.ElementType;
    label: string;
    className: string;
    iconClassName: string;
    animate?: boolean;
  }
> = {
  PENDING: {
    icon: Loader2,
    label: 'Pushing to CMS...',
    className: 'bg-amber-50 text-amber-600 ring-amber-200/60',
    iconClassName: 'text-amber-500',
    animate: true,
  },
  IN_PROGRESS: {
    icon: Loader2,
    label: 'Pushing to CMS...',
    className: 'bg-violet-50 text-violet-600 ring-violet-200/60',
    iconClassName: 'text-violet-500',
    animate: true,
  },
  COMPLETED: {
    icon: CheckCircle2,
    label: 'Pushed to CMS',
    className:
      'bg-emerald-50 text-emerald-700 ring-emerald-200/60 hover:bg-emerald-100 cursor-pointer',
    iconClassName: 'text-emerald-500',
  },
  FAILED: {
    icon: AlertCircle,
    label: 'Push failed',
    className:
      'bg-red-50 text-red-600 ring-red-200/60 hover:bg-red-100 cursor-pointer',
    iconClassName: 'text-red-400',
  },
};

interface CmsPushBadgeProps {
  action: ExternalNoteActionSchema;
}

export const CmsPushBadge: React.FC<CmsPushBadgeProps> = ({ action }) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const config = STATUS_CONFIG[action.status];
  const Icon = config.icon;

  const tooltipText =
    action.status === 'FAILED' && action.error_message
      ? `Push failed: ${action.error_message}`
      : action.status === 'COMPLETED'
        ? `Pushed to ${action.cms_type}`
        : config.label;

  const isClickable =
    action.status === 'COMPLETED' || action.status === 'FAILED';

  const handleClick = (e: React.MouseEvent) => {
    if (!isClickable) return;
    e.stopPropagation();
    setDetailOpen(true);
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                e.stopPropagation();
                setDetailOpen(true);
              }
            }}
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-px text-[10px] font-medium ring-1 ring-inset transition-colors ${config.className}`}
          >
            <Icon
              className={`h-2.5 w-2.5 ${config.iconClassName} ${config.animate ? 'animate-spin' : ''}`}
            />
            {action.status === 'COMPLETED' ? (
              'CMS'
            ) : action.status === 'FAILED' ? (
              'Failed'
            ) : (
              <span className="flex items-center gap-0.5">
                CMS
                <ArrowUpRight className="h-2 w-2" />
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={4}>
          <p className="text-[10px] max-w-48">
            {tooltipText}
            {isClickable && ' \u00B7 Click for details'}
          </p>
        </TooltipContent>
      </Tooltip>

      {isClickable && (
        <CmsPushDetailModal
          open={detailOpen}
          onOpenChange={setDetailOpen}
          action={action}
        />
      )}
    </>
  );
};
